<template>
  <div class="benefit-life">
    <el-card class="toolbar">
      <el-tabs v-model="activeEntity" @tab-click="handleEntityChange">
        <el-tab-pane v-for="item in entities" :key="item.entity" :label="item.label" :name="item.entity" />
      </el-tabs>
      <el-row :gutter="12">
        <el-col :span="6">
          <el-input v-model="filters.keyword" placeholder="关键词" clearable @keyup.enter.native="loadList" />
        </el-col>
        <el-col :span="6">
          <el-select
            v-model="filters.communityId"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="小区"
            :remote-method="remoteSearchCommunity"
            :loading="optionLoading.communityId"
            style="width: 100%"
            @focus="loadFieldOptions({ prop: 'communityId' })"
            @keyup.enter.native="loadList"
          >
            <el-option
              v-for="option in optionLists.communityId"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-col>
        <el-col :span="12" class="actions">
          <el-button type="primary" icon="el-icon-search" @click="loadList">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
          <el-button v-if="current.editable" type="success" icon="el-icon-plus" @click="openCreate">新增</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column v-for="col in current.columns" :key="col.prop" :label="col.label" min-width="120">
          <template slot-scope="scope">
            <el-image
              v-if="col.type === 'image'"
              class="table-image"
              :src="getColumnValue(col, scope.row) || '/img/noPhoto.jpg'"
              :preview-src-list="[getColumnValue(col, scope.row) || '/img/noPhoto.jpg']"
              fit="cover"
            />
            <span v-else>{{ getColumnValue(col, scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="current.editable" size="mini" @click="openEdit(scope.row)">编辑</el-button>
            <el-button v-if="activeEntity === 'order' && scope.row.state === 'WAIT_PAY'" size="mini" type="success" @click="runAction('payOrder', scope.row)">模拟支付</el-button>
            <el-button v-if="activeEntity === 'coupon'" size="mini" type="primary" @click="runAction('claimCoupon', scope.row)">模拟领取</el-button>
            <el-button v-if="activeEntity === 'mutualTask' && scope.row.state === 'PUBLISHED'" size="mini" type="primary" @click="runAction('acceptTask', scope.row)">接单</el-button>
            <el-button v-if="activeEntity === 'mutualTask' && scope.row.state === 'ACCEPTED'" size="mini" type="warning" @click="runAction('finishTask', scope.row)">完成</el-button>
            <el-button v-if="activeEntity === 'mutualTask' && scope.row.state === 'FINISHED'" size="mini" type="success" @click="runAction('confirmTask', scope.row)">确认</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page.sync="page.current"
        :page-size="page.size"
        :total="page.total"
        layout="total, prev, pager, next, jumper"
        @current-change="loadList"
      />
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="680px">
      <el-form label-width="150px">
        <el-form-item v-for="field in current.fields" :key="field.prop" :label="field.label">
          <upload-image-url
            v-if="field.type === 'image'"
            :ref="`${field.prop}Upload`"
            :image-count="1"
            @notifyUploadCoverImage="handleImageFieldUpload(field, $event)"
          />
          <el-select
            v-else-if="isOptionField(field)"
            v-model="form[field.prop]"
            filterable
            remote
            clearable
            reserve-keyword
            :placeholder="`请选择${field.label}`"
            :remote-method="remoteSearchField(field)"
            :loading="optionLoading[field.prop]"
            style="width: 100%"
            @focus="loadFieldOptions(field)"
            @change="handleOptionChange(field, $event)"
          >
            <el-option
              v-for="option in optionLists[field.prop]"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <div v-else-if="field.type === 'richText'" class="editor-wrapper">
            <rich-text-editor
              :ref="`${field.prop}Editor`"
              v-model="form[field.prop]"
            />
          </div>
          <el-input
            v-else
            v-model="form[field.prop]"
            :type="field.type || 'text'"
            :disabled="isReadOnlyField(field)"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { queryBenefitLife, saveBenefitLife, updateBenefitLife, benefitLifeAction } from '@/api/benefitLife/benefitLifeApi'
import { listAdminCommunitys, queryAdminOwners } from '@/api/owner/adminOwnerApi'
import UploadImageUrl from '@/components/upload/UploadImageUrl'
import RichTextEditor from '@/components/editor/RichTextEditor'

const fieldLabels = {
  communityId: '小区ID',
  name: '名称',
  merchantType: '商家类型',
  contactName: '联系人',
  tel: '电话',
  address: '地址',
  state: '状态',
  merchantId: '商家ID',
  categoryId: '分类ID',
  price: '售价',
  marketPrice: '市场价',
  stock: '库存',
  integralGift: '赠送积分',
  coverPhoto: '商品图片',
  productType: '商品类型',
  deliveryType: '履约方式',
  productId: '商品ID',
  groupPrice: '团购价',
  userCount: '成团人数',
  limitCount: '限购数量',
  earnRate: '赠送比例',
  deductionRate: '抵扣比例',
  maxDeductionRate: '最高抵扣',
  feeTypes: '适用费用',
  supplierId: '供应商ID',
  couponType: '卡券类型',
  faceValue: '面值',
  salePrice: '销售价',
  ownerId: '业主ID',
  ownerName: '业主姓名',
  title: '标题',
  itemType: '物品类型',
  description: '详情',
  remark: '详情',
  taskType: '任务类型',
  rewardAmount: '佣金'
}

const FIELD_OPTION_SOURCES = {
  communityId: {
    type: 'community',
    valueKey: 'communityId',
    labelKeys: ['name', 'communityName']
  },
  merchantId: {
    type: 'benefitLife',
    entity: 'merchant',
    valueKey: 'merchantId',
    labelKeys: ['name', 'merchantName'],
    dependsOnCommunity: true
  },
  categoryId: {
    type: 'benefitLife',
    entity: 'category',
    valueKey: 'categoryId',
    labelKeys: ['name', 'categoryName'],
    dependsOnCommunity: true
  },
  productId: {
    type: 'benefitLife',
    entity: 'product',
    valueKey: 'productId',
    labelKeys: ['name', 'productName'],
    dependsOnCommunity: true
  },
  supplierId: {
    type: 'benefitLife',
    entity: 'couponSupplier',
    valueKey: 'supplierId',
    labelKeys: ['name', 'supplierName']
  },
  ownerId: {
    type: 'owner',
    valueKey: 'ownerId',
    labelKeys: ['name', 'ownerName'],
    dependsOnCommunity: true
  },
  accountId: {
    type: 'benefitLife',
    entity: 'integralAccount',
    valueKey: 'accountId',
    labelKeys: ['ownerName', 'accountName', 'accountId'],
    dependsOnCommunity: true
  }
}

const OPTION_FIELD_PROPS = Object.keys(FIELD_OPTION_SOURCES)
const DEPEND_ON_COMMUNITY_PROPS = OPTION_FIELD_PROPS.filter(prop => FIELD_OPTION_SOURCES[prop].dependsOnCommunity)

const commonColumns = [
  { prop: 'communityId', label: '小区ID' },
  { prop: 'state', label: '状态' }
]

function makeField(prop) {
  const field = { prop, label: fieldLabels[prop] || prop }
  if (prop === 'coverPhoto') {
    field.type = 'image'
  }
  if (prop === 'description' || prop === 'remark') {
    field.type = 'richText'
  }
  if (FIELD_OPTION_SOURCES[prop]) {
    field.optionSource = prop
  }
  return field
}

function makeEntity(entity, label, id, columns, fields, editable = true) {
  return {
    entity,
    label,
    id,
    editable,
    columns: columns.map(([prop, colLabel, type]) => ({ prop, label: colLabel, type })).concat(commonColumns),
    fields: fields.map(makeField)
  }
}

const entities = [
  makeEntity('merchant', '商家管理', 'merchantId', [
    ['merchantId', '商家ID'], ['name', '名称'], ['merchantType', '类型'], ['tel', '电话']
  ], ['communityId', 'name', 'merchantType', 'contactName', 'tel', 'address', 'remark', 'state']),
  makeEntity('category', '商品分类', 'categoryId', [
    ['categoryId', '分类ID'], ['name', '名称'], ['seq', '排序']
  ], ['communityId', 'name', 'seq', 'state']),
  makeEntity('product', '商品管理', 'productId', [
    ['coverPhoto', '图片', 'image'], ['productId', '商品ID'], ['name', '名称'], ['merchantId', '商家ID'], ['price', '售价'], ['stock', '库存']
  ], ['communityId', 'merchantId', 'categoryId', 'name', 'coverPhoto', 'productType', 'marketPrice', 'price', 'stock', 'integralGift', 'deliveryType', 'description', 'state']),
  makeEntity('groupBuy', '团购管理', 'groupId', [
    ['groupId', '团购ID'], ['name', '名称'], ['productId', '商品ID'], ['groupPrice', '团购价'], ['userCount', '成团人数']
  ], ['communityId', 'productId', 'name', 'groupPrice', 'userCount', 'limitCount', 'description', 'state']),
  makeEntity('order', '订单管理', 'orderId', [
    ['orderId', '订单ID'], ['ownerName', '业主'], ['orderType', '类型'], ['payAmount', '支付金额']
  ], ['state'], false),
  makeEntity('integralRule', '积分规则', 'ruleId', [
    ['ruleId', '规则ID'], ['name', '名称'], ['earnRate', '赠送比例'], ['deductionRate', '抵扣比例']
  ], ['communityId', 'name', 'earnRate', 'deductionRate', 'maxDeductionRate', 'feeTypes', 'state']),
  makeEntity('integralAccount', '积分账户', 'accountId', [
    ['accountId', '账户ID'], ['ownerName', '业主'], ['amount', '积分']
  ], ['communityId', 'ownerId', 'ownerName', 'amount', 'state']),
  makeEntity('integralDetail', '积分流水', 'detailId', [
    ['detailId', '流水ID'], ['ownerName', '业主'], ['detailType', '类型'], ['amount', '积分']
  ], ['communityId', 'ownerId', 'accountId', 'detailType', 'amount', 'totalAmount', 'remark', 'state'], false),
  makeEntity('integralDeduction', '抵扣记录', 'deductionId', [
    ['deductionId', '抵扣ID'], ['ownerId', '业主ID'], ['integral', '积分'], ['amount', '金额']
  ], ['communityId', 'ownerId', 'feeId', 'feeType', 'integral', 'amount', 'state'], false),
  makeEntity('couponSupplier', '卡券供应商', 'supplierId', [
    ['supplierId', '供应商ID'], ['name', '名称'], ['supplierType', '类型']
  ], ['name', 'supplierType', 'remark', 'state']),
  makeEntity('coupon', '外部卡券', 'couponId', [
    ['couponId', '卡券ID'], ['name', '名称'], ['couponType', '类型'], ['faceValue', '面值'], ['stock', '库存']
  ], ['supplierId', 'name', 'couponType', 'faceValue', 'salePrice', 'stock', 'description', 'state']),
  makeEntity('secondhand', '二手交换', 'itemId', [
    ['itemId', '物品ID'], ['title', '标题'], ['ownerName', '业主'], ['itemType', '类型'], ['price', '价格']
  ], ['communityId', 'ownerId', 'ownerName', 'title', 'itemType', 'price', 'description', 'state']),
  makeEntity('mutualTask', '互助任务', 'taskId', [
    ['taskId', '任务ID'], ['title', '标题'], ['ownerName', '发起人'], ['rewardAmount', '佣金'], ['acceptUserName', '接单人']
  ], ['communityId', 'ownerId', 'ownerName', 'title', 'taskType', 'rewardAmount', 'description', 'state']),
  makeEntity('commission', '佣金流水', 'commissionId', [
    ['commissionId', '佣金ID'], ['taskTitle', '任务'], ['ownerName', '用户'], ['amount', '金额']
  ], ['state'], false)
]

function emptyOptionBucket() {
  return OPTION_FIELD_PROPS.reduce((bucket, prop) => {
    bucket[prop] = []
    return bucket
  }, {})
}

function emptyOptionMap() {
  return OPTION_FIELD_PROPS.reduce((bucket, prop) => {
    bucket[prop] = {}
    return bucket
  }, {})
}

function emptyLoadingMap() {
  return OPTION_FIELD_PROPS.reduce((bucket, prop) => {
    bucket[prop] = false
    return bucket
  }, {})
}

export default {
  name: 'BenefitLifeManageList',
  components: {
    UploadImageUrl,
    RichTextEditor
  },
  data() {
    return {
      activeEntity: 'merchant',
      loading: false,
      rows: [],
      filters: { keyword: '', communityId: '' },
      operator: { ownerId: 'admin', ownerName: '后台用户' },
      page: { current: 1, size: 10, total: 0 },
      dialogVisible: false,
      editing: false,
      form: {},
      entities,
      optionLists: emptyOptionBucket(),
      optionMaps: emptyOptionMap(),
      optionLoading: emptyLoadingMap()
    }
  },
  computed: {
    current() {
      return this.entities.find(item => item.entity === this.activeEntity) || this.entities[0]
    },
    dialogTitle() {
      return `${this.editing ? '编辑' : '新增'}${this.current.label}`
    }
  },
  created() {
    this.syncRouteEntity()
    this.loadFieldOptions({ prop: 'communityId' })
    this.loadList()
  },
  watch: {
    '$route'(to, from) {
      if (to.fullPath !== from.fullPath) {
        this.onRouteChange()
      }
    }
  },
  methods: {
    syncRouteEntity() {
      const entity = (this.$route && this.$route.meta && this.$route.meta.entity) || (this.$route && this.$route.query && this.$route.query.entity)
      if (entity && this.entities.some(item => item.entity === entity)) {
        this.activeEntity = entity
      }
    },
    handleEntityChange() {
      this.page.current = 1
      this.loadList()
    },
    async onRouteChange() {
      this.syncRouteEntity()
      this.page.current = 1
      await this.loadList()
    },
    async loadList() {
      this.loading = true
      try {
        const res = await queryBenefitLife({
          entity: this.activeEntity,
          page: this.page.current,
          row: this.page.size,
          ...this.filters
        })
        this.rows = res.data || []
        this.page.total = res.total || 0
        await this.ensureReferenceOptionsForRows(this.rows)
      } catch (error) {
        this.$message.error(error.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { keyword: '', communityId: '' }
      this.page.current = 1
      this.loadList()
    },
    openCreate() {
      this.editing = false
      const form = {
        entity: this.activeEntity,
        state: this.activeEntity === 'mutualTask' ? 'PUBLISHED' : 'ON'
      }
      if (this.current.fields.some(field => field.prop === 'communityId')) {
        form.communityId = this.filters.communityId || ''
      }
      this.form = form
      this.dialogVisible = true
      this.$nextTick(() => {
        this.syncImageUploads()
        this.syncRichTextEditors()
        this.loadDialogOptions()
      })
    },
    openEdit(row) {
      this.editing = true
      const form = { entity: this.activeEntity }
      form[this.current.id] = this.getRowValue(row, this.current.id)
      this.current.fields.forEach(field => {
        form[field.prop] = this.getRowValue(row, field.prop)
      })
      this.form = form
      this.dialogVisible = true
      this.$nextTick(async() => {
        this.syncImageUploads()
        this.syncRichTextEditors()
        await this.ensureReferenceOptionsForForm()
        this.loadDialogOptions()
      })
    },
    async submitForm() {
      try {
        if (this.editing) {
          await updateBenefitLife(this.form)
        } else {
          await saveBenefitLife(this.form)
        }
        this.dialogVisible = false
        this.loadList()
      } catch (error) {
        this.$message.error(error.message || 'Save failed')
      }
    },
    syncImageUploads() {
      this.current.fields
        .filter(field => field.type === 'image')
        .forEach(field => {
          const refs = this.$refs[`${field.prop}Upload`]
          const upload = Array.isArray(refs) ? refs[0] : refs
          if (upload && upload.setImages) {
            upload.setImages(this.form[field.prop] ? [this.form[field.prop]] : [])
          }
        })
    },
    handleImageFieldUpload(field, images) {
      this.$set(this.form, field.prop, images && images.length > 0 ? images[0] : '')
    },
    syncRichTextEditors() {
      this.current.fields
        .filter(field => field.type === 'richText')
        .forEach(field => {
          const refs = this.$refs[`${field.prop}Editor`]
          const editor = Array.isArray(refs) ? refs[0] : refs
          if (editor && editor.setContent) {
            editor.setContent(this.form[field.prop] || '')
          }
        })
    },
    async runAction(action, row) {
      try {
        await benefitLifeAction({
          action,
          taskId: row.taskId,
          orderId: row.orderId,
          couponId: row.couponId,
          communityId: row.communityId,
          ownerId: row.ownerId || this.operator.ownerId,
          ownerName: row.ownerName || this.operator.ownerName,
          acceptUserId: this.operator.ownerId,
          acceptUserName: this.operator.ownerName
        })
        this.loadList()
      } catch (error) {
        this.$message.error(error.message || '操作失败')
      }
    },
    remoteSearchCommunity(keyword) {
      this.loadFieldOptions({ prop: 'communityId' }, keyword)
    },
    remoteSearchField(field) {
      return keyword => this.loadFieldOptions(field, keyword)
    },
    isOptionField(field) {
      return Boolean(field && field.optionSource)
    },
    isReadOnlyField(field) {
      return field.prop === 'ownerName' && this.current.fields.some(item => item.prop === 'ownerId')
    },
    isReferenceColumn(prop) {
      return FIELD_OPTION_SOURCES[prop] && prop !== this.current.id
    },
    getColumnValue(col, row) {
      const value = this.getRowValue(row, col.prop)
      if (this.isReferenceColumn(col.prop)) {
        return this.getOptionLabel(col.prop, value, row)
      }
      return value
    },
    getRowValue(row, prop) {
      if (!row || !prop) {
        return ''
      }
      const value = row[prop]
      if (value !== undefined && value !== null) {
        return value
      }
      const snakeValue = row[this.toSnake(prop)]
      return snakeValue === undefined || snakeValue === null ? '' : snakeValue
    },
    getOptionLabel(fieldOrProp, value, row) {
      if (value === undefined || value === null || value === '') {
        return ''
      }
      const prop = typeof fieldOrProp === 'string' ? fieldOrProp : fieldOrProp.prop
      const source = FIELD_OPTION_SOURCES[prop]
      if (!source) {
        return value
      }
      const rowLabel = this.getFirstValue(row, source.labelKeys)
      if (rowLabel) {
        return rowLabel
      }
      const cached = this.optionMaps[prop] && this.optionMaps[prop][value]
      return cached ? cached.label : value
    },
    getFirstValue(row, keys) {
      if (!row || !keys) {
        return ''
      }
      for (const key of keys) {
        const value = this.getRowValue(row, key)
        if (value) {
          return value
        }
      }
      return ''
    },
    async loadDialogOptions() {
      const fields = this.current.fields.filter(field => this.isOptionField(field))
      await Promise.all(fields.map(field => this.loadFieldOptions(field)))
    },
    async loadFieldOptions(field, keyword = '') {
      const prop = field.prop
      const source = FIELD_OPTION_SOURCES[prop]
      if (!source) {
        return
      }
      this.$set(this.optionLoading, prop, true)
      try {
        let rows = []
        if (source.type === 'benefitLife') {
          const params = {
            entity: source.entity,
            page: 1,
            row: 50
          }
          if (keyword) {
            params.keyword = keyword
          }
          if (source.dependsOnCommunity && this.form.communityId) {
            params.communityId = this.form.communityId
          }
          const res = await queryBenefitLife(params)
          rows = res.data || []
        } else if (source.type === 'community') {
          const res = await listAdminCommunitys({
            page: 1,
            row: 100,
            name: keyword,
            communityName: keyword
          })
          rows = res.data || []
        } else if (source.type === 'owner') {
          const params = {
            page: 1,
            row: 50,
            name: keyword
          }
          const communityId = this.form.communityId || this.filters.communityId
          if (source.dependsOnCommunity && communityId) {
            params.communityId = communityId
          }
          const res = await queryAdminOwners(params)
          rows = res.data || []
        }
        const options = rows.map(row => this.normalizeOption(prop, row)).filter(option => option.value)
        this.$set(this.optionLists, prop, options)
        options.forEach(option => this.cacheOption(prop, option))
      } catch (error) {
        this.$message.error(error.message || '加载选项失败')
      } finally {
        this.$set(this.optionLoading, prop, false)
      }
    },
    normalizeOption(prop, row) {
      const source = FIELD_OPTION_SOURCES[prop]
      const value = this.getRowValue(row, source.valueKey)
      const label = this.getFirstValue(row, source.labelKeys) || value
      return {
        value,
        label,
        raw: row
      }
    },
    cacheOption(prop, option) {
      if (!option || !option.value) {
        return
      }
      this.$set(this.optionMaps[prop], option.value, option)
    },
    handleOptionChange(field, value) {
      if (field.prop === 'communityId') {
        this.clearCommunityDependentFields()
        this.loadDialogOptions()
        return
      }
      if (field.prop === 'ownerId') {
        const option = this.optionMaps.ownerId[value]
        this.$set(this.form, 'ownerName', option ? option.label : '')
      }
    },
    clearCommunityDependentFields() {
      DEPEND_ON_COMMUNITY_PROPS.forEach(prop => {
        if (prop !== 'communityId' && this.form[prop]) {
          this.$set(this.form, prop, '')
        }
      })
      if (this.form.ownerName) {
        this.$set(this.form, 'ownerName', '')
      }
    },
    async ensureReferenceOptionsForRows(rows) {
      if (!rows || rows.length === 0) {
        return
      }
      const props = this.current.columns
        .map(col => col.prop)
        .filter(prop => this.isReferenceColumn(prop))
      await this.ensureReferenceOptions(props, rows)
    },
    async ensureReferenceOptionsForForm() {
      const rows = [this.form]
      const props = this.current.fields
        .map(field => field.prop)
        .filter(prop => FIELD_OPTION_SOURCES[prop])
      await this.ensureReferenceOptions(props, rows)
    },
    async ensureReferenceOptions(props, rows) {
      const tasks = []
      props.forEach(prop => {
        rows.forEach(row => {
          const value = this.getRowValue(row, prop)
          if (!value || this.optionMaps[prop][value]) {
            return
          }
          const source = FIELD_OPTION_SOURCES[prop]
          const rowLabel = this.getFirstValue(row, source.labelKeys)
          if (rowLabel) {
            this.cacheOption(prop, { value, label: rowLabel, raw: row })
            return
          }
          tasks.push(this.loadReferenceOption(prop, value, row))
        })
      })
      await Promise.all(tasks)
    },
    async loadReferenceOption(prop, value, row) {
      const source = FIELD_OPTION_SOURCES[prop]
      try {
        let rows = []
        if (source.type === 'benefitLife') {
          const params = {
            entity: source.entity,
            page: 1,
            row: 1
          }
          params[source.valueKey] = value
          const res = await queryBenefitLife(params)
          rows = res.data || []
        } else if (source.type === 'community') {
          if (this.optionLists.communityId.length === 0) {
            await this.loadFieldOptions({ prop: 'communityId' })
          }
          return
        } else if (source.type === 'owner') {
          const params = {
            page: 1,
            row: 1,
            ownerId: value
          }
          const communityId = this.getRowValue(row, 'communityId') || this.form.communityId || this.filters.communityId
          if (communityId) {
            params.communityId = communityId
          }
          const res = await queryAdminOwners(params)
          rows = res.data || []
        }
        if (rows.length > 0) {
          this.cacheOption(prop, this.normalizeOption(prop, rows[0]))
        }
      } catch (error) {
        // The UI falls back to the raw ID when a reference cannot be resolved.
      }
    },
    toSnake(prop) {
      return prop.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    }
  }
}
</script>

<style scoped>
.benefit-life {
  padding: 20px;
}
.toolbar {
  margin-bottom: 16px;
}
.actions {
  text-align: right;
}
.el-pagination {
  margin-top: 16px;
  text-align: right;
}
.table-image {
  width: 64px;
  height: 64px;
  border-radius: 4px;
}
.editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}
</style>
